// dateCore — the single source of truth for the variable-precision date model:
// month tables, tolerant paste parsing, precision inference, ISO build, range/day
// validation bounds, display formatting, and the bridge between the widget's
// partial-ISO value ({date, dateEnd, ongoing}) and the DB column form.
//
// Pure, dependency-free, framework-agnostic — consumed by the DateEntry widget
// (input), the chronology views (display), and any other module that shows or
// edits a legal date. Ported from the Claude Design handoff `chronology-datecore.js`.
//
// Two precision representations:
//   • parts  v = {y,m,d} (number|null)        → precision(v) ∈ year|month|day|gap|empty
//   • string "2021" / "2021-03" / "2021-03-15" → granularity == precision

export type Precision = 'day' | 'month' | 'year';

/** The widget value: partial-ISO start/end (granularity is precision) + ongoing. */
export interface DateValue {
  date: string;       // '' | 'YYYY' | 'YYYY-MM' | 'YYYY-MM-DD'
  dateEnd: string;    // '' unless a closed range
  ongoing: boolean;   // open-ended range (start, no end)
}

/** The persisted column form (mirrors chronology_events). event_date is padded to
 *  first-of-period, end_date to last-of-period; precision carries the granularity. */
export interface DateColumns {
  event_date: string | null;
  event_precision: Precision | null;
  end_date: string | null;
  end_precision: Precision | null;
  is_ongoing: boolean;
}

export interface Parts { y: number | null; m: number | null; d: number | null }
export type PartsPrecision = Precision | 'gap' | 'empty';

export interface ParsedPaste {
  y?: string; m?: string; d?: string;
  bad?: boolean; raw?: string;
  ambiguous?: boolean; a?: string; b?: string;
}

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export interface MonthMeta { n: number; pad: string; abbr: string; full: string; tokens: string[] }
export const META: MonthMeta[] = MONTHS.map((m, i) => {
  const n = i + 1;
  const pad = String(n).padStart(2, '0');
  return { n, pad, abbr: m, full: FULL[i], tokens: [String(n), pad, m.toLowerCase(), FULL[i].toLowerCase()] };
});

export const monthAbbr = (pad: string): string => META[parseInt(pad, 10) - 1].abbr;

export function monthFromName(s: string): MonthMeta | null {
  s = s.toLowerCase().replace(/\.$/, '');
  return META.find((mm) => mm.full.toLowerCase() === s || mm.abbr.toLowerCase() === s)
    || (s.length >= 3 ? META.find((mm) => mm.full.toLowerCase().startsWith(s)) ?? null : null)
    || null;
}

export const leap = (y: number): boolean => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
export const dim = (y: number, m: number): number =>
  [31, leap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];

/** A real proleptic-Gregorian calendar day in the URL/API wire format. */
export function isValidIsoDay(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  return y >= 1 && m >= 1 && m <= 12 && d >= 1 && d <= dim(y, m);
}

export function expandYear(s: string): string {
  if (s.length > 2) return s.slice(0, 4);
  const yy = +s;
  return String(yy <= 68 ? 2000 + yy : 1900 + yy); // POSIX-style pivot
}

/** Tolerant: find a date inside messy pasted text (stray words, whitespace, odd dashes). */
export function parsePasted(raw: string): ParsedPaste | null {
  if (!raw) return null;
  const t = raw.replace(/[‐-―−]/g, '-'); // normalize en/em dashes & minus
  let m: RegExpMatchArray | null;
  m = t.match(/(?:^|[^\d])(\d{4})\s*[-/.]\s*(\d{1,2})(?:\s*[-/.]\s*(\d{1,2}))?(?!\d)/); // ISO YYYY-MM(-DD)
  if (m) {
    // Reject an out-of-range month/day rather than passing '13'/'00' downstream
    // (which would silently coerce or, via the swapbar, index outside META).
    const mm = +m[2], dd = m[3] ? +m[3] : null;
    if (mm >= 1 && mm <= 12 && (dd === null || (dd >= 1 && dd <= 31))) {
      return { y: m[1], m: String(mm).padStart(2, '0'), d: dd === null ? undefined : String(dd).padStart(2, '0') };
    }
  }
  // Day↔year separator is `[\s,-]+` (not `,?[\s-]+`): tolerant of whitespace
  // AROUND the comma, so PDF-mangled captions like "August 14 , 2023" parse.
  // Strict superset of the old form — every previously-accepted separator
  // (optional comma + ≥1 space/hyphen) is still a run of [\s,-].
  m = t.match(/([A-Za-z]{3,9})\.?[\s-]+(\d{1,2})(?:st|nd|rd|th)?[\s,-]+(\d{4}|\d{2})/); // March 05, 2026 / "Aug 14 , 2023" / Mar-5-26
  if (m) { const mo = monthFromName(m[1]); if (mo) return { y: expandYear(m[3]), m: mo.pad, d: m[2] }; }
  m = t.match(/(\d{1,2})(?:st|nd|rd|th)?[\s-]+([A-Za-z]{3,9})\.?[\s,-]+(\d{4}|\d{2})/); // 5 March 2026 / "14 Aug , 2023" / 3-Mar-2026
  if (m) { const mo = monthFromName(m[2]); if (mo) return { y: expandYear(m[3]), m: mo.pad, d: m[1] }; }
  m = t.match(/(?:^|[^\d])(\d{1,2})\s*[-/.]\s*(\d{1,2})\s*[-/.]\s*(\d{4}|\d{2})(?!\d)/); // a/b/YY(YY)
  if (m) {
    const a = +m[1], b = +m[2], y = expandYear(m[3]);
    // 0 or >31 in either slot is not a real day/month — reject (don't let a '00'
    // reach the swapbar's monthAbbr and index outside META).
    if (a < 1 || b < 1 || a > 31 || b > 31) return { bad: true, raw };
    if (a > 12 && b > 12) return { bad: true, raw };
    if (a > 12) return { y, m: String(b).padStart(2, '0'), d: String(a).padStart(2, '0') };
    if (b > 12) return { y, m: String(a).padStart(2, '0'), d: String(b).padStart(2, '0') };
    return { y, ambiguous: true, a: String(a).padStart(2, '0'), b: String(b).padStart(2, '0') };
  }
  m = t.match(/([A-Za-z]{3,9})\.?[\s-]+(\d{4})/); // March 2026
  if (m) { const mo = monthFromName(m[1]); if (mo) return { y: m[2], m: mo.pad }; }
  m = t.match(/(?:^|[^\d])(\d{1,2})\s*[-/.]\s*(\d{4})(?!\d)/); // MM/YYYY
  if (m) { const mm = +m[1]; if (mm >= 1 && mm <= 12) return { y: m[2], m: String(mm).padStart(2, '0') }; }
  m = t.match(/(?:^|[^\d])(\d{4})(?!\d)/); // bare year
  if (m) return { y: m[1] };
  return null;
}

// ----- parts model (input side) -----
export function precision(v: Parts): PartsPrecision {
  if (v.d != null) return v.m != null && v.y != null ? 'day' : 'gap';
  if (v.m != null) return v.y != null ? 'month' : 'gap';
  if (v.y != null) return 'year';
  return 'empty';
}
export function iso(v: Parts, p: PartsPrecision): string {
  if (p === 'year') return String(v.y);
  if (p === 'month') return v.y + '-' + String(v.m).padStart(2, '0');
  if (p === 'day') return v.y + '-' + String(v.m).padStart(2, '0') + '-' + String(v.d).padStart(2, '0');
  return '';
}
export const lb = (v: Parts, p: PartsPrecision): number =>
  (v.y || 0) * 1e4 + (p === 'year' ? 1 : (v.m || 1)) * 1e2 + (p === 'day' ? (v.d || 1) : 1);
export const ub = (v: Parts, p: PartsPrecision): number => {
  const y = v.y || 0, m = p === 'year' ? 12 : (v.m || 12), d = p === 'day' ? (v.d || 1) : dim(y, m);
  return y * 1e4 + m * 1e2 + d;
};

// ----- month digit buffer (smooth "type the digits" month entry) -----
// The month field accepts a number typed digit-by-digit. These pure helpers drive
// the buffer state machine the MonthCombobox runs on each keystroke, so the
// auto-advance rules are testable without React. The buffer is only ever '', '0',
// or '1' between calls (a complete two-digit month commits and resets it).

export interface MonthDigitResult {
  /** The next pending buffer ('' once a month commits). */
  buffer: string;
  /** commit → resolve `month` now and advance; pending → wait for more input;
   *  reject → ignore this keystroke (keep the current buffer). */
  status: 'commit' | 'pending' | 'reject';
  /** Padded '01'…'12' when status === 'commit'. */
  month?: string;
}

/** Given the current pending digit buffer and a freshly-typed digit, decide
 *  whether the month is now unambiguously complete (commit + advance), still
 *  ambiguous (pending), or the keystroke is impossible (reject). */
export function classifyMonthDigit(buf: string, digit: string): MonthDigitResult {
  if (!/^[0-9]$/.test(digit)) return { buffer: buf, status: 'reject' };
  if (buf === '') {
    const n = +digit;
    // 2–9 can only be a single-digit month (no two-digit month starts 2–9).
    if (n >= 2) return { buffer: '', status: 'commit', month: '0' + digit };
    // '0' (start of 01–09) and '1' (Jan, or start of 10/11/12) stay ambiguous.
    return { buffer: digit, status: 'pending' };
  }
  if (buf === '1') {
    // Forming 10 / 11 / 12; any other second digit is an impossible month.
    if (+digit <= 2) return { buffer: '', status: 'commit', month: '1' + digit };
    return { buffer: buf, status: 'reject' };
  }
  if (buf === '0') {
    // Forming 01–09; '00' is not a month.
    if (+digit >= 1) return { buffer: '', status: 'commit', month: '0' + digit };
    return { buffer: buf, status: 'reject' };
  }
  return { buffer: buf, status: 'reject' };
}

/** Drain a pending single-digit buffer to a padded month on a non-digit resolve
 *  trigger (separator / Enter / Tab / the pause timeout): '1' → '01' (January),
 *  '2'…'9' → '0N'; a lone '0' is not a month → '' (commit nothing). */
export function resolvePendingMonth(buf: string): string {
  if (!buf || buf === '0') return '';
  const n = +buf;
  return n >= 1 && n <= 12 ? String(n).padStart(2, '0') : '';
}

// ----- string model (display side); precision == granularity -----
export function fmtSingle(s: string): string {
  const p = (s || '').split('-'), y = +p[0];
  if (!y) return s || '';
  if (p.length === 1) return '' + y;
  if (p.length === 2) return MONTHS[+p[1] - 1] + ' ' + y;
  return MONTHS[+p[1] - 1] + ' ' + (+p[2]) + ', ' + y;
}
/** sticky-header helper: "Apr 18" / "Apr" / "" by granularity */
export function fmtMonthDay(s: string): string {
  const p = (s || '').split('-');
  if (p.length >= 3) return MONTHS[+p[1] - 1] + ' ' + (+p[2]);
  if (p.length === 2) return MONTHS[+p[1] - 1];
  return '';
}
export const isRange = (e: DateValue): boolean => !!e.dateEnd || !!e.ongoing;
export function fmtRange(e: DateValue): string {
  if (e.ongoing) return fmtSingle(e.date) + ' →';
  if (!e.dateEnd) return fmtSingle(e.date);
  const a = (e.date || '').split('-'), b = (e.dateEnd || '').split('-');
  if (a.length === 3 && b.length === 3 && a[0] === b[0] && a[1] === b[1])
    return MONTHS[+a[1] - 1] + ' ' + (+a[2]) + '–' + (+b[2]) + ', ' + a[0];
  if (a.length === 3 && b.length === 3 && a[0] === b[0])
    return MONTHS[+a[1] - 1] + ' ' + (+a[2]) + ' – ' + MONTHS[+b[1] - 1] + ' ' + (+b[2]) + ', ' + a[0];
  if (a.length === 2 && b.length === 2 && a[0] === b[0])
    return MONTHS[+a[1] - 1] + ' – ' + MONTHS[+b[1] - 1] + ' ' + a[0];
  if (a.length === 1 && b.length === 1)
    return a[0] === b[0] ? a[0] : a[0] + '–' + b[0];
  return fmtSingle(e.date) + ' – ' + fmtSingle(e.dateEnd);
}

// ----- bridge: partial-ISO value <-> DB column form -----
/** Granularity of a partial-ISO string ('2021'→year, '2021-03'→month, '2021-03-15'→day). */
export function granularity(partialIso: string): Precision {
  const n = (partialIso || '').split('-').length;
  return n >= 3 ? 'day' : n === 2 ? 'month' : 'year';
}
/** Truncate a full/partial ISO down to the given precision. */
export function truncate(isoStr: string, p: Precision): string {
  if (!isoStr) return '';
  return p === 'year' ? isoStr.slice(0, 4) : p === 'month' ? isoStr.slice(0, 7) : isoStr.slice(0, 10);
}
/** Pad a partial-ISO to a full date at the FIRST instant of its period. */
export function padFirst(partialIso: string): string {
  const [y, m, d] = (partialIso || '').split('-');
  if (!y) return '';
  return y + '-' + (m || '01') + '-' + (d || '01');
}
/** Pad a partial-ISO to a full date at the LAST instant of its period. */
export function padLast(partialIso: string): string {
  const [y, m, d] = (partialIso || '').split('-');
  if (!y) return '';
  if (d) return y + '-' + m + '-' + d;
  if (m) return y + '-' + m + '-' + String(dim(+y, +m)).padStart(2, '0');
  return y + '-12-31';
}

/** Widget value → DB columns. Empty start ⟹ undated (everything null). */
export function columnsFromValue(v: DateValue): DateColumns {
  if (!v.date) {
    return { event_date: null, event_precision: null, end_date: null, end_precision: null, is_ongoing: false };
  }
  const event_precision = granularity(v.date);
  const event_date = padFirst(v.date);
  if (v.ongoing) {
    return { event_date, event_precision, end_date: null, end_precision: null, is_ongoing: true };
  }
  if (v.dateEnd) {
    return { event_date, event_precision, end_date: padLast(v.dateEnd), end_precision: granularity(v.dateEnd), is_ongoing: false };
  }
  return { event_date, event_precision, end_date: null, end_precision: null, is_ongoing: false };
}

/** DB columns → widget value (partial-ISO at each end's stored precision). */
export function valueFromColumns(c: Partial<DateColumns> | null | undefined): DateValue {
  if (!c || !c.event_date || !c.event_precision) {
    return { date: '', dateEnd: '', ongoing: false };
  }
  const date = truncate(c.event_date, c.event_precision);
  if (c.is_ongoing) return { date, dateEnd: '', ongoing: true };
  const dateEnd = c.end_date && c.end_precision ? truncate(c.end_date, c.end_precision) : '';
  return { date, dateEnd, ongoing: false };
}
